import React, { Fragment, useEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";

export type TOOLTIP_PLACEMENT = "right-start" | "bottom-end" | "bottom-start" | "bottom" | "left-end" | "left-start" | "left" | "right-end" | "right" | "top-end" | "top-start" | "top"

const TableSpotlightOverlay = ({ columnSelector, rowSelector, tooltip, customInTable = null, tooltipPlacement = "right-start" as TOOLTIP_PLACEMENT, tooltipTargetSelector = null, brearVisible = false, clickable = false, onButtonClick = () => { }, bearFile = null, isDarkMode = false }) => {
    const [maskRect, setMaskRect] = useState<{
        top: number;
        left: number;
        width: number;
        height: number;
        rowTop: number;
        rowHeight: number;
    } | null>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const [tooltipAnchor, setTooltipAnchor] = useState<Element | null>(null);

    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null;
        let mutationObserver: MutationObserver | null = null;
        let pollingInterval: NodeJS.Timeout | null = null;

        const findBearElement = () => {
            if (brearVisible) {
                const bearEl = document.querySelector('img[alt="tutorialcurebear"]');
                if (bearEl) {
                    setTooltipAnchor(bearEl);
                    if (pollingInterval) {
                        clearInterval(pollingInterval);
                        pollingInterval = null;
                    }
                }
            }
        };

        const updateRect = () => {
            const colEl = document.querySelector(columnSelector);
            const rowEl = document.querySelector(rowSelector);
            const targetEl = tooltipTargetSelector ? document.querySelector(tooltipTargetSelector) : null;

            if (brearVisible) {
                findBearElement();
            } else {
                setTooltipAnchor(targetEl || anchorRef.current);
            }

            if (colEl && rowEl) {
                const colRect = colEl.getBoundingClientRect();
                const rowRect = rowEl.getBoundingClientRect();

                const top = Math.min(colRect.top, rowRect.top) + window.scrollY;
                const left = Math.min(colRect.left, rowRect.left) + window.scrollX;
                const bottom = Math.max(colRect.bottom, rowRect.bottom) + window.scrollY;
                const right = Math.max(colRect.right, rowRect.right) + window.scrollX;
                const rowTop = rowRect.top + window.scrollY + 20;
                const rowHeight = rowRect.height;

                const newRect = {
                    top: top - 4,
                    left: left - (-4),
                    width: right - left + 8,
                    height: bottom - top + 12,
                    rowTop,
                    rowHeight,
                };

                setMaskRect((prev) => {
                    if (
                        !prev ||
                        prev.top !== newRect.top ||
                        prev.left !== newRect.left ||
                        prev.width !== newRect.width ||
                        prev.height !== newRect.height ||
                        prev.rowTop !== newRect.rowTop ||
                        prev.rowHeight !== newRect.rowHeight
                    ) {
                        return newRect;
                    }
                    return prev;
                });

                // Setup ResizeObserver only once
                if (!resizeObserver) {
                    resizeObserver = new ResizeObserver(updateRect);
                    resizeObserver.observe(colEl);
                    resizeObserver.observe(rowEl);
                }

                // Observe DOM changes that could affect layout
                if (!mutationObserver) {
                    mutationObserver = new MutationObserver(() => {
                        updateRect();
                        findBearElement();
                    });
                    mutationObserver.observe(document.body, {
                        attributes: true,
                        childList: true,
                        subtree: true,
                    });
                }
            }
        };

        // Start polling for bear element if needed
        if (brearVisible && !pollingInterval) {
            pollingInterval = setInterval(findBearElement, 100);
        }

        updateRect();
        window.addEventListener("resize", updateRect);
        window.addEventListener("scroll", updateRect);

        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener("resize", updateRect);
            window.removeEventListener("scroll", updateRect);
        };
    }, [columnSelector, rowSelector, tooltipTargetSelector, brearVisible]);

    if (!maskRect) return null;

    const overlayStyle = {
        position: "fixed" as const,
        backgroundColor: "transparent",
        zIndex: 9999,
        pointerEvents: "none" as const,
    };
    if (clickable) {
        delete overlayStyle.pointerEvents;
    }
    const handleClick = () => {
        if (clickable) {
            onButtonClick();
        }
    }

    return (
        <>
            {/* Dark overlays */}
            <div style={{ ...overlayStyle, top: 0, left: 0, width: "100vw", height: maskRect.top, backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top + maskRect.height, left: 0, width: "100vw", height: "100vh", backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top, left: 0, width: maskRect.left, height: maskRect.height, backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top, left: maskRect.left + maskRect.width, right: 0, height: maskRect.height, zIndex: 9998, backdropFilter: "blur(1px)" }} onClick={handleClick} />

            {/* Spotlight with rounded border */}
            <div
                style={{
                    ...overlayStyle,
                    position: "absolute",
                    top: maskRect.top,
                    left: maskRect.left,
                    width: maskRect.width,
                    height: customInTable ? "auto" : maskRect.height,
                    borderRadius: "12px",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
                    pointerEvents: "none",
                    zIndex: 9999,
                }}
                onClick={handleClick}
            >
                {customInTable && (
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        minHeight: "100%",
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "stretch",
                        background: "#FFF",
                        borderRadius: "12px",
                    }}>
                        {customInTable}
                    </div>
                )}
            </div>

            {/* Anchor point for tooltip */}
            <div
                ref={anchorRef}
                style={{
                    position: "absolute",
                    top: maskRect.rowTop,
                    left: tooltipPlacement.includes('top') ? maskRect.left + (maskRect.width / 2) : maskRect.left + maskRect.width,
                    width: 1,
                    height: 1,
                    pointerEvents: "none",
                }}
                onClick={handleClick}
            />

            {/* Tooltip */}
            {tooltipAnchor && (
                <Tooltip
                    open
                    placement={tooltipPlacement}
                    title={
                        <Paper sx={{ borderRadius: "12px" }} onClick={handleClick}>
                            {tooltip}
                        </Paper>
                    }
                    arrow
                    sx={{
                        "& .MuiTooltip-popper": {
                            background: isDarkMode ? "var(--dark-background)" : "red",
                            "& .MuiTooltip-tooltipPlacementRight": {
                                background: isDarkMode ? "var(--dark-background)" : "red",
                                "& .MuiTooltip-arrow": {
                                    height: "2.5em",
                                    width: "1.5em",
                                }

                            },
                        }

                    }}
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: isDarkMode ? "var(--dark-background)" : "#FFF",
                                zIndex: 10000,
                                borderRadius: "12px",
                                padding: 0,
                                '& .MuiTooltip-arrow': {
                                    color: isDarkMode ? "var(--dark-text-primary)" : '#FFF',
                                },
                                maxWidth: "350px",
                                "&.MuiTooltip-tooltipPlacementRight": {
                                    "& .MuiTooltip-arrow": {
                                        height: "2.5em",
                                        width: "1.5em",
                                        marginLeft: "-1.51em",
                                        marginTop:"2em"
                                    }
                                },
                                "&.MuiTooltip-tooltipPlacementBottom": {
                                    "& .MuiTooltip-arrow": {
                                        height: "1.5em",
                                        width: "2.5em",
                                        marginTop: "-1.51em",
                                    }
                                },
                                "&.MuiTooltip-tooltipPlacementTop": {
                                    "& .MuiTooltip-arrow": {
                                        height: "1.5em",
                                        width: "2.5em",
                                        marginBottom: "-1.51em",
                                        ...(isDarkMode ? {
                                            "&:before": {
                                                backgroundColor: "var(--dark-border-color)",
                                            },
                                        } : {})
                                     
                                    }
                                },
                            },
                        },
                        arrow: {
                            sx: {
                                color: isDarkMode ? "var(--dark-text-primary)" : "#fff",
                                zIndex: 10000,
                                ...(isDarkMode ? {
                                    "&:before": {
                                        backgroundColor: "#383838",
                                    },
                                } : {})
                            },
                        },
                    }}
                    PopperProps={{
                        anchorEl: tooltipAnchor,
                        disablePortal: true,
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: tooltipPlacement === 'right' ? [10, 0] :
                                        tooltipPlacement === 'right-start' ? [-20, 15] :
                                            tooltipPlacement === 'right-end' ? [-20, 15] :
                                                tooltipPlacement.includes('left') ? [-10, 0] :
                                                    tooltipPlacement.includes('bottom') ? [0, 20] :tooltipPlacement.includes('top') ? [0, 30] : [0, -10],
                                },
                            },
                            {
                                name: 'preventOverflow',
                                enabled: true,
                                options: {
                                    altAxis: true,
                                    altBoundary: true,
                                    tether: true,
                                    rootBoundary: 'document',
                                    padding: 8,
                                },
                            },
                            {
                                name: 'zIndexFix',
                                enabled: true,
                                phase: 'beforeWrite',
                                fn: ({ state }) => {
                                    state.styles.popper.zIndex = "10000";
                                },
                            },
                        ],
                    }}
                >
                    <span style={{ position: "absolute", top: -9999 }} />
                </Tooltip>
            )}
            {brearVisible && <Box position={"fixed"} bottom={-5} left={"60px"} zIndex={10001}>
                <img src={bearFile} alt="tutorialcurebear" onClick={handleClick} />
            </Box>}
        </>
    );
};

export default TableSpotlightOverlay;